import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useDebouncedValue } from "@/lib/search";
import { errorId, useFieldErrors } from "@/lib/formErrors";
import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { FieldError, invalidClass } from "@/components/FieldError";
import { FieldLabel } from "@/components/FieldLabel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const DIGITS = [3, 4, 5, 6, 7, 8];
const RESTARTS = {
  calendar_year: ["every 1 January", "the year"],
  financial_year: ["every 1 April", "the financial year"],
  calendar_and_financial_year: ["every 1 January and every 1 April", "both the year and the financial year"],
};

// What each part prints today, so the chips show a value rather than only a name.
function partsForToday() {
  const today = new Date();
  const year = today.getFullYear();
  const fyStart = today.getMonth() >= 3 ? year : year - 1;
  return [
    { token: "{YYYY}", means: "year", sample: String(year) },
    { token: "{YY}", means: "short year", sample: String(year % 100).padStart(2, "0") },
    { token: "{FY}", means: "financial year", sample: `${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}` },
    { token: "{SEQ}", means: "the count", sample: "0001" },
  ];
}

const numberIn = (series, seq, digits) => series.replace("{SEQ}", String(seq).padStart(digits, "0"));

/**
 * The format certificate numbers are issued under. Every number is still allocated by the server,
 * one at a time and never twice; this edits the format and how far on the next one is. What a
 * format means — the next number, when the count starts again — is asked of the server while HR
 * types, so the screen can never describe a rule the server does not apply.
 */
export default function CertificateNumbering({ saved, writable, onSaved }) {
  const [draft, setDraft] = useState({ pattern: saved.pattern, digits: saved.digits });
  const [next, setNext] = useState("");
  const [desc, setDesc] = useState(saved);
  const [liveErrors, setLiveErrors] = useState({});
  const [checkFailed, setCheckFailed] = useState(false);
  const [checkAgain, setCheckAgain] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { errors, report, clear } = useFieldErrors();
  const patternBox = useRef(null);
  const asked = useRef(0);
  const parts = partsForToday();

  // A save, or the section loading again, is the new starting point.
  useEffect(() => {
    setDraft({ pattern: saved.pattern, digits: saved.digits });
    setDesc(saved);
    setNext("");
    setLiveErrors({});
    setCheckFailed(false);
  }, [saved]);

  const settled = useDebouncedValue(draft, 350);
  const settledIsSaved = settled.pattern === saved.pattern && Number(settled.digits) === saved.digits;
  const draftIsSaved = draft.pattern === saved.pattern && Number(draft.digits) === saved.digits;

  useEffect(() => {
    const mine = ++asked.current;
    if (settledIsSaved) {
      setDesc(saved);
      setLiveErrors({});
      setCheckFailed(false);
      return;
    }
    api.post("/settings/certificate-numbering/preview", { pattern: settled.pattern, digits: Number(settled.digits) })
      .then((r) => {
        if (mine !== asked.current) return;
        if (!r.data || typeof r.data.next_number !== "string") throw new Error("unexpected response");
        setDesc(r.data);
        setLiveErrors({});
        setCheckFailed(false);
      })
      .catch((e) => {
        if (mine !== asked.current) return;
        // A refusal is an answer — this format cannot work, and its box says why. Anything else
        // means the check never ran, which must not read as the format being fine.
        const fields = e.response?.data?.fields;
        setLiveErrors(fields || {});
        setCheckFailed(!fields);
        setDesc(null);
      });
  }, [settled, settledIsSaved, saved, checkAgain]);

  const shown = { ...liveErrors, ...errors };
  const typedNext = next.trim();
  const nextSeq = /^\d{1,8}$/.test(typedNext) ? Number(typedNext) : null;
  const current = desc;
  const tooLow = !!(current && nextSeq !== null && nextSeq < current.next_seq);
  const jump = !!(current && nextSeq !== null && nextSeq > current.next_seq);
  const checking = !draftIsSaved && (settled !== draft || (!desc && !checkFailed && !Object.keys(liveErrors).length));
  const dirty = !draftIsSaved || typedNext !== "";

  const insert = (token) => {
    const box = patternBox.current;
    const value = draft.pattern;
    const start = box?.selectionStart ?? value.length;
    const end = box?.selectionEnd ?? start;
    setDraft({ ...draft, pattern: value.slice(0, start) + token + value.slice(end) });
    clear("pattern");
    requestAnimationFrame(() => {
      box?.focus();
      box?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    if (jump && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    setSaving(true);
    try {
      const r = await api.put("/settings/certificate-numbering", {
        pattern: draft.pattern, digits: Number(draft.digits), ...(typedNext ? { next_number: typedNext } : {}),
      });
      if (!r.data || typeof r.data.next_number !== "string") throw new Error("unexpected response");
      clear();
      onSaved(r.data);
      toast.success(`Numbering saved — the next certificate will be ${r.data.next_number}`);
    } catch (e) {
      report(e, { fallback: "The numbering could not be saved.", known: ["pattern", "digits", "next_number"] });
    }
    setSaving(false);
  };

  const discard = () => {
    setDraft({ pattern: saved.pattern, digits: saved.digits });
    setNext("");
    clear();
  };

  const blocked = saving || !dirty || checking || tooLow || checkFailed || Object.keys(liveErrors).length > 0;
  const restart = current && RESTARTS[current.resets];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] gap-6">
      <fieldset className="space-y-4 border-0 p-0 m-0 min-w-0" disabled={!writable}>
        <div>
          <FieldLabel htmlFor="num-pattern" required>Format</FieldLabel>
          <Input id="num-pattern" ref={patternBox} value={draft.pattern} spellCheck={false} autoComplete="off"
                 onChange={(e) => { setDraft({ ...draft, pattern: e.target.value }); clear("pattern"); }}
                 className={cn("font-mono", invalidClass(shown, "pattern"))} maxLength={60}
                 data-field="pattern" aria-invalid={shown.pattern ? true : undefined}
                 aria-describedby={shown.pattern ? errorId("pattern") : "num-pattern-hint"}
                 data-testid="num-pattern" />
          <FieldError field="pattern" errors={shown} />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {parts.map((p) => (
              <button key={p.token} type="button" onClick={() => insert(p.token)}
                      title={`Insert ${p.token} — ${p.means}`}
                      data-testid={`num-part-${p.token.slice(1, -1).toLowerCase()}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs transition-colors hover:border-primary/60 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50">
                <span className="font-mono text-foreground">{p.token}</span>
                <span className="text-muted-foreground">{p.means} · {p.sample}</span>
              </button>
            ))}
          </div>
          <p id="num-pattern-hint" className="text-xs text-muted-foreground mt-2">
            Letters, digits and <span className="font-mono">/ - _ .</span> — no spaces. <span className="font-mono">{"{SEQ}"}</span> is
            the part that counts up, and appears once.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="num-digits">Digits in the count</Label>
            <Select value={String(draft.digits)} disabled={!writable}
                    onValueChange={(v) => { setDraft({ ...draft, digits: Number(v) }); clear("digits"); }}>
              <SelectTrigger id="num-digits" data-testid="num-digits" data-field="digits"
                             aria-invalid={shown.digits ? true : undefined}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIGITS.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} digits · {"1".padStart(d, "0")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError field="digits" errors={shown} />
          </div>
          <div>
            <Label htmlFor="num-next">Next number</Label>
            <Input id="num-next" inputMode="numeric" autoComplete="off" value={next}
                   placeholder={current ? String(current.next_seq) : ""}
                   onChange={(e) => { setNext(e.target.value.replace(/\D/g, "").slice(0, 8)); clear("next_number"); }}
                   className={invalidClass(shown, "next_number")} data-field="next_number"
                   aria-invalid={shown.next_number || tooLow ? true : undefined}
                   aria-describedby={shown.next_number ? errorId("next_number") : "num-next-hint"}
                   data-testid="num-next" />
            <FieldError field="next_number" errors={shown} />
            <p id="num-next-hint" className="text-xs text-muted-foreground mt-1" data-testid="num-next-hint">
              {tooLow ? (
                <span className="text-destructive-text">It can only move forward: {current.next_seq} is the lowest.</span>
              ) : jump ? (
                <>Skips {numberIn(current.series, current.next_seq, current.digits)} to {numberIn(current.series, nextSeq - 1, current.digits)} — they will never be issued.</>
              ) : (
                <>Leave blank to carry on counting. Set it only to move the count forward, for example to continue a paper register.</>
              )}
            </p>
          </div>
        </div>

        {writable && (
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={save} disabled={blocked} data-testid="num-save">
              {saving ? "Saving..." : "Save numbering"}
            </Button>
            {dirty && !saving && (
              <Button type="button" variant="ghost" onClick={discard} data-testid="num-discard">Discard changes</Button>
            )}
          </div>
        )}
      </fieldset>

      <div className="rounded-md border border-border bg-muted/30 p-5 space-y-3 min-w-0 self-start" data-testid="num-summary">
        <div className="overline text-primary">{draftIsSaved && !jump ? "Next certificate" : "Next certificate, once saved"}</div>
        <div aria-live="polite">
          {current ? (
            <div className="space-y-3">
              <div className={cn("font-mono text-2xl font-semibold tracking-tight break-all", checking && "opacity-50")}
                   data-testid="num-next-number">
                {jump ? numberIn(current.series, nextSeq, current.digits) : current.next_number}
              </div>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li data-testid="num-restarts">
                  {restart ? (
                    <>Starts again at <span className="font-mono text-foreground">{current.restart_number}</span> on{" "}
                      {fmtDate(current.restarts_on)} — {restart[0]}, because the number carries {restart[1]}.</>
                  ) : (
                    <>The count <span className="font-medium text-foreground">never starts again</span> — one continuous series.</>
                  )}
                </li>
                <li>
                  {current.highest_issued > 0 ? (
                    <>Highest issued in this series: <span className="font-mono text-foreground">{numberIn(current.series, current.highest_issued, current.digits)}</span>.</>
                  ) : (
                    <>Nothing issued in this series yet{draftIsSaved ? "" : " — a new format starts its own count"}.</>
                  )}
                </li>
                {current.skipped && (
                  <li>{current.skipped[0]} to {current.skipped[1]} were skipped and will never be issued.</li>
                )}
                {!draftIsSaved && (
                  <li className="text-foreground">Certificates already issued keep their numbers.</li>
                )}
              </ul>
            </div>
          ) : checkFailed ? (
            <div className="text-sm" data-testid="num-check-failed">
              <div className="text-destructive-text">Could not check this format.</div>
              <div className="text-xs text-muted-foreground mt-0.5">It cannot be saved until it has been checked.</div>
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setCheckAgain((n) => n + 1)}>
                Try again
              </Button>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground" data-testid="num-pending">
              {Object.keys(liveErrors).length ? "Fix the format to see the next number." : "Checking..."}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {jump && `Skip ${numberIn(current.series, current.next_seq, current.digits)} to ${numberIn(current.series, nextSeq - 1, current.digits)}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Those numbers will never be issued, and the certificate register will show the jump. The
              audit log records who moved the count and when. It cannot be moved back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep counting from {current?.next_seq}</AlertDialogCancel>
            <AlertDialogAction onClick={save} data-testid="num-confirm-skip">Skip them</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
